import { ReactSelectValue } from 'types/forms';

import Icon from 'components/common/Icon';
import Tooltip from 'components/common/Tooltip';

import { FULL_SERVICE, HYBRID } from '../SurveyTypePanel/SurveyTypePanel';
import Alert from 'components/common/Alert';
import SurveyDesignAndFielding from './SurveyDesignAndFielding';
import MethodologyAndFieldwork from './MethodologyAndFieldwork';
import { BillingPrice, BillingServiceName } from 'services/backend/billing';
import SingleChoiceButtons from 'components/DesignSystem/SingleChoiceButtons/SingleChoiceButtons';

const REPORTING_AND_ANALYSIS_OPTIONS: ReactSelectValue<string>[] = [
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' },
];

interface SurveyOptionsProps {
  surveyType: string;
  methodologyComplexity: string;
  methodologyComplexityOptions: ReactSelectValue<string>[] | undefined;
  setMethodologyComplexity: (arg: string) => void;
  methodologyFieldworkGroups: string;
  metholologyFieldworkGroupsOptions: ReactSelectValue<string>[] | undefined;
  setMethodologyFieldworkGroups: (arg: string) => void;
  reportingAndAnalysis: string;
  setReportingAndAnalysis: (arg: string) => void;
  surveyDetailsData: {
    ordering: number;
    label: string;
    value: BillingServiceName;
    tooltip: string;
    subtitle: string;
    isSelected: boolean;
    onToggle: () => void;
  }[];
  activePrices: BillingPrice[] | undefined;
  isLoadingBreakdown: boolean;
}

const fullServiceBannerItemsLayout = (
  copy: {
    label: string;
    tooltip: string;
  },
  index: number,
) => (
  <span
    key={`full-service-banner-item-${index}`}
    className="flex items-center gap-1 mt-2"
  >
    <div className="flex w-5 h-5 justify-center items-center">
      <div className="w-3 h-3 text-[#2A4F4B]">
        <Icon id="check" />
      </div>
    </div>
    {copy.label}
    <Tooltip
      trigger={
        <div className="flex w-3 h-3 text-gray-500 hover:text-gray-700">
          <Icon id="info-circle" />
        </div>
      }
    >
      {copy.tooltip}
    </Tooltip>
  </span>
);

const FullServiceDesignBanner = ({
  surveyDetailsData,
}: {
  surveyDetailsData: {
    ordering: number;
    label: string;
    tooltip: string;
    subtitle: string;
  }[];
}) => (
  <div>
    <Alert
      color="success"
      supportingText={
        <span className="flex gap-4 text-[#2A4F4B]">
          <span>
            {surveyDetailsData
              .filter((e, i) => i % 2 === 0)
              .map((copy, index) => fullServiceBannerItemsLayout(copy, index))}
          </span>
          <span>
            {surveyDetailsData
              .filter((e, i) => i % 2 !== 0)
              .map((copy, index) => fullServiceBannerItemsLayout(copy, index))}
          </span>
        </span>
      }
    >
      <span className="text-[#1A3836]">
        All design & fielding services included
      </span>
    </Alert>
    <p className="text-[#6A7282] text-xs mt-2">
      Reporting options will be available in the next step.
    </p>
  </div>
);

const SurveyOptionsPanel = ({
  surveyType,
  methodologyComplexity,
  methodologyComplexityOptions,
  setMethodologyComplexity,
  methodologyFieldworkGroups,
  metholologyFieldworkGroupsOptions,
  setMethodologyFieldworkGroups,
  reportingAndAnalysis,
  setReportingAndAnalysis,
  surveyDetailsData,
  activePrices,
  isLoadingBreakdown,
}: SurveyOptionsProps) => {
  return (
    <div className="flex flex-grow flex-col rounded-lg shadow p-8 text-left h-fit gap-4 bg-white">
      <div>
        <p className="font-bold text-2xl">Survey options</p>
      </div>
      {surveyType === FULL_SERVICE && (
        <>
          <MethodologyAndFieldwork
            methodologyComplexity={methodologyComplexity}
            methodologyComplexityOptions={methodologyComplexityOptions}
            methodologyFieldworkGroups={methodologyFieldworkGroups}
            metholologyFieldworkGroupsOptions={
              metholologyFieldworkGroupsOptions
            }
            setMethodologyComplexity={setMethodologyComplexity}
            setMethodologyFieldworkGroups={setMethodologyFieldworkGroups}
          />
          <FullServiceDesignBanner surveyDetailsData={surveyDetailsData} />
        </>
      )}
      {surveyType === HYBRID && (
        <>
          <MethodologyAndFieldwork
            methodologyComplexity={methodologyComplexity}
            methodologyComplexityOptions={methodologyComplexityOptions}
            methodologyFieldworkGroups={methodologyFieldworkGroups}
            metholologyFieldworkGroupsOptions={
              metholologyFieldworkGroupsOptions
            }
            setMethodologyComplexity={setMethodologyComplexity}
            setMethodologyFieldworkGroups={setMethodologyFieldworkGroups}
          />
          <hr className="my-3 border-[#F3F4F6]" />
          <SurveyDesignAndFielding
            activePrices={activePrices}
            isLoadingBreakdown={isLoadingBreakdown}
            surveyDetailsData={surveyDetailsData}
          />
          <>
            <hr className="my-6 border-[#F3F4F6]" />
            <p className="font-bold text-xl">Reporting & Analysis</p>
            <p className="text-sm text-gray-500 mt-1 mb-3">
              Do you want any analysis or reporting done for you by Glass?
            </p>
            <SingleChoiceButtons
              onSelect={setReportingAndAnalysis}
              options={REPORTING_AND_ANALYSIS_OPTIONS}
              selected={reportingAndAnalysis}
            />
          </>
        </>
      )}
    </div>
  );
};

export default SurveyOptionsPanel;
